const React = require('react');
const T = require('prop-types');
const { connect } = require('react-redux');
const { context } = require('app-context');
const DetailPage = require('../components/DetailPage');
const withPropTypeDependencies = require('utils/withPropTypeDependencies');
const { dependencyPropTypes } = require('../profile-dependencies');
const { push: Push, replace: Replace } = require('connected-react-router');
const { safeWarning } = require('assertions-simplified');
const CreateCommunicationService = require('services/communication');
const LeaderboardNavigation = require('utils/leaderboardNavigation');

const selectors = context.selectors.all;
const actions   = context.actions;
const redux     = context.redux.hooks;
const handlers  = context.handlers;
const communication = CreateCommunicationService.fromContext(context);
const leaderboardScope = (state) => {

    const user = selectors.getCurrentUserObject(state);
    return { schoolId: selectors.getCurrentSchoolId(state), userId: user?.id, roleId: user?.roleId };
};

const normalizeInterestList = (interests) => {

    return Array.isArray(interests) ? interests.filter(Boolean) : [];
};

const normalizeEntityList = (items) => {

    return Array.isArray(items) ? items.filter(Boolean) : [];
};

const getInterestCategoryName = (interest, categories = []) => {

    if (interest?.categoryName) {
        return interest.categoryName;
    }

    if (interest?.category?.name) {
        return interest.category.name;
    }

    const category = categories.find(({ id }) => id === interest?.categoryId);

    return category?.name || '';
};

const normalizeProfileInterestList = (interests, categories = []) => normalizeInterestList(interests).map((interest) => ({
    ...interest,
    categoryName: getInterestCategoryName(interest, categories),
    name: interest.name || ''
}));

const mergePassionsIntoInterests = (interests, passionInterests) => {

    const interestIds = new Set(interests.map(({ id }) => id).filter(Boolean));

    return interests.concat(passionInterests.filter(({ id }) => {

        if (!id) {
            return true;
        }

        if (interestIds.has(id)) {
            return false;
        }

        interestIds.add(id);

        return true;
    }));
};

const normalizeUserDetailsForProfile = (userDetails, categories = []) => {

    if (!userDetails) {
        return userDetails;
    }

    const userInterests = normalizeProfileInterestList(userDetails.user?.interests, categories);
    const interests = normalizeProfileInterestList(userDetails.interests?.length ? userDetails.interests : userInterests, categories);
    const passionInterests = normalizeProfileInterestList(userDetails.passionInterests, categories);

    return {
        ...userDetails,
        classes: normalizeEntityList(userDetails.classes),
        peers: normalizeEntityList(userDetails.peers),
        interests: mergePassionsIntoInterests(interests, passionInterests),
        passionInterests
    };
};

const getFallbackCategoryName = (interest = {}) => interest.categoryName
    || interest.category?.name
    || 'Interests';

const buildProfileCategories = ({ categories = [], userDetails }) => {

    const baseCategories = normalizeEntityList(categories);
    const categoriesById = new Map(baseCategories.map((category) => [String(category.id), category]));
    const fallbackCategories = [];

    normalizeInterestList(userDetails?.interests)
        .concat(normalizeInterestList(userDetails?.passionInterests))
        .forEach((interest) => {

            if (!interest?.categoryId) {
                return;
            }

            const categoryKey = String(interest.categoryId);

            if (categoriesById.has(categoryKey)) {
                return;
            }

            const fallbackCategory = {
                id: interest.categoryId,
                name: getFallbackCategoryName(interest),
                special: !!interest.special,
                svg: interest.category?.svg || interest.svg || null
            };

            categoriesById.set(categoryKey, fallbackCategory);
            fallbackCategories.push(fallbackCategory);
        });

    return baseCategories.concat(fallbackCategories);
};

const idsKey = (items) => {

    return normalizeInterestList(items)
        .map(({ id }) => id)
        .filter(Boolean)
        .join(',');
};

const getProfileDependencyInstanceKey = (userId, userDetails) => {

    if (!userDetails) {
        return `profile:${userId}:empty`;
    }

    return [
        'profile',
        userId,
        idsKey(userDetails.classes),
        idsKey(userDetails.peers),
        idsKey(userDetails.interests),
        idsKey(userDetails.passionInterests),
        userDetails.hometown?.name || ''
    ].join(':');
};

const isValidDirectMessageChannelSid = (channelSid) => {

    return typeof channelSid === 'string' && /^CH[a-zA-Z0-9]+$/.test(channelSid);
};

const resolveDirectMessageChannelSid = async ({ currentUserId, otherUserId, dispatch }) => {

    const getExistingChannelSid = async () => {

        const { result: existingChannelSid, error } = await communication.getDMChannelByUsers(
            currentUserId,
            otherUserId
        );

        return !error && isValidDirectMessageChannelSid(existingChannelSid) ? existingChannelSid : null;
    };

    const existingChannelSid = await getExistingChannelSid();

    if (existingChannelSid) {
        return existingChannelSid;
    }

    const createdConversation = await dispatch(actions.communication.createTwilioConversation({
        user1Id: currentUserId,
        user2Id: otherUserId
    }));

    const createdChannelSid = createdConversation?.payload?.result?.sid
        || createdConversation?.payload?.result?.result?.sid;

    if (isValidDirectMessageChannelSid(createdChannelSid)) {
        return createdChannelSid;
    }

    return getExistingChannelSid();
};

const preloadDirectMessageRoute = async ({ channelSid, otherUserId, dispatch, getState }) => {

    if (!selectors.getUser_forDetails(getState(), otherUserId)) {
        await dispatch(actions.dataFetching.fetchUser({ id: otherUserId }));
    }

    await handlers.communication.startListening(channelSid);

    if (selectors.getTwilioChannelsLoading(getState())) {
        await dispatch(actions.dataFetching.fetchChannels());
    }
};

class DetailPageWithBackgroundFetch extends React.PureComponent {

    componentDidMount() {

        this.fetchUserDetails();
    }

    componentDidUpdate(prevProps) {

        if (prevProps.profileUserId !== this.props.profileUserId) {
            this.fetchUserDetails();
        }
    }

    fetchUserDetails = () => {

        const {
            profileUserId,
            fetchUserDetailsInBackground
        } = this.props;

        if (profileUserId) {
            // The school-level matchingDebug flag controls visibility; do not require
            // users to append a debug query parameter to profile URLs.
            fetchUserDetailsInBackground(profileUserId, true);
        }
    }

    render() {

        const {
            fetchUserDetailsInBackground,
            profileUserId,
            ...props
        } = this.props;

        return <DetailPage {...props} />;
    }
}

DetailPageWithBackgroundFetch.propTypes = {
    profileUserId: T.oneOfType([T.number, T.string]),
    fetchUserDetailsInBackground: T.func.isRequired
};

const DetailPageContainer = connect(
    (state, { match: { params: { userId } }, location }) => {

        const groupNavigation = selectors.getGroupNavigation(state);
        const categories = selectors.getCategories(state) || [];
        const userDetails = selectors.getUser_forDetails(state, userId);
        const targetUserRole = selectors.getUserRole(state, userId);
        const userDetailsWithRole = userDetails && targetUserRole && !userDetails.user?.role?.id
            ? {
                ...userDetails,
                user: {
                    ...userDetails.user,
                    role: targetUserRole
                }
            }
            : userDetails;
        const normalizedUserDetails = normalizeUserDetailsForProfile(userDetailsWithRole, categories);
        const profileCategories = buildProfileCategories({
            categories,
            userDetails: normalizedUserDetails
        });

        const searchParams = new URLSearchParams(location.search);
        const leaderboard = LeaderboardNavigation.profileContext(leaderboardScope(state), location.search, userId);
        const fromDiscover = !!leaderboard || searchParams.get('fromDiscover') === 'true';
        const fromGroup = searchParams.get('fromGroup') === 'true';
        const groupId = searchParams.get('groupId');
        const index = searchParams.get('index');

        return {
            key: userId,
            dependencyInstanceKey: getProfileDependencyInstanceKey(userId, normalizedUserDetails),
            profileUserId: userId,
            userDetails: normalizedUserDetails,
            currentUser: selectors.getCurrentUser(state)?.user(),
            currentSchoolId: selectors.getCurrentSchoolId(state),
            interestsLoading: !normalizedUserDetails && selectors.getIsLoadingInterests(state),
            targetUserRole,
            selectedUserIsStudentGroup: selectors.isStudentRoleGroup(state, userId),
            currentCommunityUnreadCounts: selectors.getCurrentCommunityUnreadCounts(state),
            categories: profileCategories,
            currentUserClasses: selectors.getCurrentUser(state).classes() || [],
            currentUserInterests: selectors.getCurrentUserInterests(state) || [],
            rolePermissions: selectors.getCurrentUserRolePermissions(state),
            rolesInteractions: selectors.getCurrentUserRoleInteractions(state),
            role: selectors.getCurrentUserRole(state),
            isCompany: selectors.getSchoolIsCompany(state),
            isCommunity: selectors.getSchoolIsCommunity(state),
            isSchoolOnline: selectors.getSchoolIsOnline(state),
            badges: selectors.getAllBadges(state),
            discoverNavigation: leaderboard ? { currentResultIndex: leaderboard.index, fromDiscover: true } : selectors.getDiscoverNavigation(state),
            discoveryResults: leaderboard ? leaderboard.ids.map((id) => ({ id })) : selectors.getUsers_forUserSearch(state) || [],
            hasMoreDiscoverResults: leaderboard ? false : selectors.getShowLoadMore_forUserSearch(state),
            loadingNextPage: selectors.getIsLoadingNextPage_forUserSearch(state),
            groupNavigation,
            groupMembers: groupNavigation.groupId
                ? (selectors.getGroup_forGroupDetail_withUserSearchResults(state, groupNavigation.groupId)?.users || [])
                : [],
            debugGroupSorting: selectors.getDebug(state).groupSorting,
            urlContext: {
                fromDiscover,
                fromLeaderboard: !!leaderboard,
                fromGroup,
                groupId: groupId ? parseInt(groupId) : null,
                index: index ? parseInt(index) : null
            }
        };
    },
    {
    fetchUserDetailsInBackground: (userId, debugMatching = false) => (dispatch, getState) => {

            const state = getState();
            const currentUserId = selectors.getCurrentUserId(state);

            if (String(userId) === String(currentUserId) && selectors.getUser_forDetails(state, userId)) {
                return Promise.resolve();
            }

            return dispatch(actions.dataFetching.fetchUser({ id: userId, debugMatching }))
                .catch(safeWarning);
        },
        onClickChat: (userId, message) => actions.communication.requestChat({ userId, message }),
        onClickConnect: (userId, message, isAutoConnect, similarityText) => actions.communication.requestPeer({ userId, message, isAutoConnect, similarityText }),
        onRequestDisconnect: ({ id: userId, firstName }) => actions.communication.disconnect({ userId, firstName }),
        onAcceptPeer: ({ id: userId, firstName }) => actions.communication.acceptPeer({ userId, firstName }),
        onEnableUser: ({ id: userId }, toEnable) => actions.communication.enableUser({ userId, toEnable }),
        goBack: () => {

            return (dispatch, getState) => {

                const scope = leaderboardScope(getState());
                if (LeaderboardNavigation.isLeaderboard(scope, window.location.search)) {
                    const period = new URLSearchParams(window.location.search).get('leaderboardPeriod') === 'allTime' ? 'allTime' : 'weekly';
                    LeaderboardNavigation.write(scope, { view: true, period });
                    return dispatch(Replace('/app/discover'));
                }
                window.history.go(-3);
            };
        },
        push: (...args) => async (dispatch, getState) => {

            const [path] = args;
            const directMessageUserPathPrefix = '/app/messaging/u:';

            if (typeof path !== 'string' || !path.startsWith(directMessageUserPathPrefix)) {
                return dispatch(Push(...args));
            }

            const otherUserId = path.slice(directMessageUserPathPrefix.length);
            const currentUserId = selectors.getCurrentUserId(getState());

            try {
                const channelSid = await resolveDirectMessageChannelSid({
                    currentUserId,
                    otherUserId,
                    dispatch
                });

                if (channelSid) {
                    await preloadDirectMessageRoute({
                        channelSid,
                        otherUserId,
                        dispatch,
                        getState
                    });

                    return dispatch(Push(`/app/messaging/${channelSid}`));
                }
            }
            catch (err) {
                safeWarning(err);
            }

            return dispatch(actions.alerts.notification.push({
                message: 'Sorry, we could not open this chat. Please try again.'
            }));
        },
        onNavigateToProfile: function (userId, newIndex, shouldAutoLoad = false) {

            return async function (dispatch, getState) {

                const scope = leaderboardScope(getState());
                if (LeaderboardNavigation.isLeaderboard(scope, window.location.search)) {
                    const saved = LeaderboardNavigation.read(scope);
                    const target = saved.ids[newIndex];
                    if (target && target === Number(userId)) {
                        dispatch(Replace(LeaderboardNavigation.profileUrl(target, newIndex, saved.period)));
                    }
                    return;
                }

                if (shouldAutoLoad) {
                    await dispatch(actions.dataFetching.fetchDiscoverSearchResults.nextPage());
                    const newState = getState();
                    const updatedResults = selectors.getUsers_forUserSearch(newState) || [];
                    if (updatedResults[newIndex]) {
                        dispatch(actions.app.setDiscoverNavigation({
                            currentResultIndex: newIndex,
                            fromDiscover: true
                        }));
                        dispatch(actions.app.clearGroupNavigation());
                        dispatch(Replace(`/app/profile/${updatedResults[newIndex].id}?fromDiscover=true&index=${newIndex}`));
                    }

                    return;
                }

                dispatch(actions.app.setDiscoverNavigation({
                    currentResultIndex: newIndex,
                    fromDiscover: true
                }));
                dispatch(actions.app.clearGroupNavigation());
                dispatch(Replace(`/app/profile/${userId}?fromDiscover=true&index=${newIndex}`));
            };
        },
        onNavigateToGroupProfile: function (userId, newIndex) {

            return function (dispatch, getState) {

                const state = getState();
                const groupId = selectors.getGroupNavigationGroupId(state);

                dispatch(actions.app.setGroupNavigation({
                    currentResultIndex: newIndex,
                    fromGroup: true,
                    groupId
                }));
                dispatch(actions.app.clearDiscoverNavigation());

                dispatch(Replace(`/app/profile/${userId}?fromGroup=true&groupId=${groupId}&index=${newIndex}`));
            };
        },
        showNotification: (msg) => actions.alerts.notification.push({ message: msg }),
        saveProfilePicture: (profilePictureDetails) => (dispatch, getState) => { // eslint-disable-line

            const profilePictureDiff = selectors.getUserProfilePictureDiff_forUpdateProfile(redux.getState(), profilePictureDetails);

            dispatch(actions.app.setShouldRefreshSearch({ discover: true, group: true }));

            return dispatch(actions.profileManagement.updateProfilePicture(profilePictureDiff))
                .then((success) => {

                    // The action creator resolves undefined on failure
                    if (!success) {
                        throw new Error('Update profile picture failed');
                    }

                    dispatch(actions.alerts.notification.push({
                        message: 'Profile picture updated'
                    }));

                    return success;
                })
                .catch(safeWarning);
        }
    }
)(withPropTypeDependencies(DetailPageWithBackgroundFetch, {
    propTypes: dependencyPropTypes,
    timeoutMs: 30000
}));

DetailPageContainer.dependencyPropTypes = dependencyPropTypes;

module.exports = DetailPageContainer;
